/* oxlint-disable no-underscore-dangle */
/**
 * Config resolved at RUNTIME (build-once → promote dev→staging→prod strategy).
 *
 * - Web/PWA: the container injects `window.__APP_CONFIG__` via `/config.js` (generated
 *   by nginx's entrypoint from environment variables on startup). A single image
 *   serves all environments; only the container's env changes.
 * - Tauri (desktop/mobile): there's no `/config.js`; falls back to the baked-in values of
 *   `import.meta.env.VITE_*` (build-time) → behavior unchanged.
 * - Local dev: same (import.meta.env from apps/web/.env) or fallback to localhost.
 */
interface AppRuntimeConfig {
  SERVER_URL?: string;
  POWERSYNC_URL?: string;
  /**
   * Active vocabulary profile (`src/i18n/vocabulary/`): what words this
   * institution uses for the student group and the academic period. It's declared
   * here so it stays in the SAME list as the rest of the runtime config,
   * but it is NOT resolved in this module: `activeVocabularyProfileKey()` reads it
   * lazily, because the profile is consulted when each catalog loads and the
   * tests need to be able to change it without re-importing the whole module.
   */
  VOCABULARY_PROFILE?: string;
}

const runtime: AppRuntimeConfig =
  (globalThis as typeof globalThis & { __APP_CONFIG__?: AppRuntimeConfig }).__APP_CONFIG__ ?? {};

/**
 * First value that is REALLY present. Plain `??` isn't enough: the entrypoint
 * writes `"KEY": ""` for any env the container doesn't define, and an empty
 * string isn't nullish → it would beat the default and leave, e.g., an empty
 * base URL. Here "unconfigured" and "empty" are the same thing.
 */
function firstConfigured(...values: (string | undefined)[]): string | undefined {
  return values.find((v) => v !== undefined && v.trim() !== "")?.trim();
}

/** Backend base (auth/api/server). */
export const SERVER_URL: string =
  firstConfigured(
    runtime.SERVER_URL,
    import.meta.env.VITE_SERVER_URL,
    import.meta.env.VITE_API_URL,
  ) ?? "http://localhost:3333";

/** PowerSync service endpoint. */
export const POWERSYNC_URL: string =
  firstConfigured(runtime.POWERSYNC_URL, import.meta.env.VITE_POWERSYNC_URL) ??
  "http://localhost:8085";
